import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { statements } from "@/mocks/statements";

export default function StatementDetail() {
  const { id } = useParams<{ id: string }>();
  const statement = statements.find((item) => item.id === id);
  const [copied, setCopied] = useState(false);

  const related = useMemo(
    () => (statement ? statement.relatedIds.map((rid) => statements.find((s) => s.id === rid)).filter(Boolean) : []),
    [statement],
  );

  const jsonLd = useMemo(() => {
    if (!statement) return undefined;
    return [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: statement.title,
        description: statement.summary,
        datePublished: statement.date,
        dateModified: statement.date,
        inLanguage: "zh-CN",
        articleSection: statement.categoryName,
        keywords: statement.tags.join(","),
        author: { "@type": "Organization", name: statement.source },
        publisher: { "@type": "Organization", name: "黑料官方" },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "声明公告库", item: "/statements" },
          { "@type": "ListItem", position: 3, name: statement.title, item: `/statements/${statement.id}` },
        ],
      },
    ];
  }, [statement]);

  useSeo({
    title: statement ? `${statement.title} - 黑料官方${statement.categoryName}` : "公告未找到 - 黑料官方",
    description: statement
      ? `${statement.summary}（${statement.categoryName} · ${statement.date} 发布）`
      : "未找到对应的官方公告，可返回声明公告库查看全部归档内容。",
    keywords: statement ? statement.tags.join(",") : "黑料官方",
    path: statement ? `/statements/${statement.id}` : "/statements",
    ogType: "article",
    jsonLd,
  });

  if (!statement) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-24">
          <div className="max-w-lg rounded-lg border border-background-200 bg-background-100 px-8 py-14 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
              <i className="ri-file-unknow-line" />
            </span>
            <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950">未找到该公告</h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              公告可能已调整归档地址，或链接有误。你可返回声明公告库重新检索。
            </p>
            <Link
              to="/statements"
              className="mt-6 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              返回声明公告库
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const archiveNo = `HL-${statement.date.replace(/-/g, "")}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <article>
          <header className="border-b border-background-200 bg-background-100">
            <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
              <Breadcrumbs
                items={[
                  { label: "首页", to: "/" },
                  { label: "声明公告库", to: "/statements" },
                  { label: statement.categoryName, to: `/statements?category=${statement.categoryId}` },
                  { label: statement.title },
                ]}
              />
              <div className="mt-6 flex flex-wrap items-center gap-3 text-xs">
                <Link
                  to={`/statements?category=${statement.categoryId}`}
                  className="rounded-full bg-primary-500 px-3 py-1 font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                >
                  {statement.categoryName}
                </Link>
                <time dateTime={statement.date} className="text-foreground-600">发布于 {statement.date}</time>
                <span className="text-foreground-500">归档编号 {archiveNo}</span>
              </div>
              <h1 className="mt-4 max-w-4xl font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
                {statement.title}
              </h1>
              <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
                {statement.summary}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={copyLink}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700 cursor-pointer"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className={copied ? "ri-check-line" : "ri-link"} />
                  </span>
                  {copied ? "链接已复制" : "复制公告链接"}
                </button>
                <Link
                  to="/statements"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600 cursor-pointer"
                >
                  <span className="flex h-4 w-4 items-center justify-center"><i className="ri-arrow-left-line" /></span>
                  返回公告库
                </Link>
              </div>
            </div>
          </header>

          <div className="mx-auto grid max-w-content grid-cols-1 gap-10 px-4 py-12 md:px-6 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="h-64 w-full overflow-hidden rounded-lg border border-background-200 bg-background-200 md:h-80">
                <img
                  src={statement.cover}
                  alt={statement.title}
                  title={`${statement.title} - 黑料官方公告配图`}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <div className="mt-8 rounded-lg border-l-4 border-accent-500 bg-accent-50 p-6">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-accent-900">
                  <span className="flex h-5 w-5 items-center justify-center"><i className="ri-verified-badge-line" /></span>
                  核实结论
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-accent-900/90">{statement.verdict}</p>
              </div>

              <div className="mt-10 space-y-10">
                {statement.sections.map((section, index) => (
                  <section key={section.heading} id={`sec-${index + 1}`} className="scroll-mt-24">
                    <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                      <span className="mr-3 text-primary-500">{String(index + 1).padStart(2, "0")}</span>
                      {section.heading}
                    </h2>
                    <div className="mt-4 space-y-4 border-l border-background-200 pl-5">
                      {section.paragraphs.map((paragraph) => (
                        <p key={paragraph} className="text-[15px] leading-8 text-foreground-700">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </section>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                <span className="text-sm text-foreground-500">相关标签</span>
                {statement.tags.map((tag) => (
                  <span key={tag} className="rounded-md bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                    {tag}
                  </span>
                ))}
              </div>

              {related.length > 0 && (
                <section aria-labelledby="related-title" className="mt-12">
                  <h2 id="related-title" className="font-heading text-xl font-bold text-foreground-950">
                    相关公告
                  </h2>
                  <div className="mt-4 flex flex-col divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
                    {related.map((item) =>
                      item ? (
                        <Link
                          key={item.id}
                          to={`/statements/${item.id}`}
                          className="group flex flex-col gap-2 p-5 transition-colors hover:bg-background-100 cursor-pointer sm:flex-row sm:items-center sm:justify-between"
                        >
                          <span className="flex flex-col gap-1">
                            <span className="font-heading text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-700">
                              {item.title}
                            </span>
                            <span className="text-xs text-foreground-500">{item.categoryName} · {item.date}</span>
                          </span>
                          <span className="flex h-6 w-6 flex-none items-center justify-center text-foreground-400 group-hover:text-primary-600">
                            <i className="ri-arrow-right-line" />
                          </span>
                        </Link>
                      ) : null,
                    )}
                  </div>
                </section>
              )}
            </div>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24">
                <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                  <h2 className="font-heading text-base font-bold text-foreground-950">公告信息</h2>
                  <dl className="mt-4 space-y-3 text-sm">
                    <div className="flex items-start justify-between gap-4">
                      <dt className="text-foreground-500">类别</dt>
                      <dd className="text-right text-foreground-900">{statement.categoryName}</dd>
                    </div>
                    <div className="flex items-start justify-between gap-4">
                      <dt className="text-foreground-500">发布时间</dt>
                      <dd className="text-right text-foreground-900">{statement.date}</dd>
                    </div>
                    <div className="flex items-start justify-between gap-4">
                      <dt className="text-foreground-500">发布渠道</dt>
                      <dd className="text-right text-foreground-900">{statement.source}</dd>
                    </div>
                    <div className="flex items-start justify-between gap-4">
                      <dt className="text-foreground-500">归档编号</dt>
                      <dd className="text-right text-foreground-900">{archiveNo}</dd>
                    </div>
                  </dl>
                </div>

                <nav aria-label="内容目录" className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6">
                  <h2 className="font-heading text-base font-bold text-foreground-950">公告要点</h2>
                  <ol className="mt-4 space-y-2.5 text-sm">
                    {statement.sections.map((section, index) => (
                      <li key={section.heading}>
                        <a
                          href={`#sec-${index + 1}`}
                          className="flex items-start gap-2 text-foreground-600 transition-colors hover:text-primary-700"
                        >
                          <span className="mt-0.5 flex h-4 w-4 flex-none items-center justify-center text-xs text-foreground-400">
                            {index + 1}
                          </span>
                          {section.heading}
                        </a>
                      </li>
                    ))}
                  </ol>
                </nav>

                <div className="mt-6 rounded-lg border border-primary-200 bg-primary-50 p-6">
                  <h2 className="font-heading text-base font-bold text-primary-800">还有疑问？</h2>
                  <p className="mt-2 text-sm leading-relaxed text-primary-900/80">
                    如果该公告未覆盖你遇到的问题，可通过线索提交入口补充材料，我们会在核实后更新说明。
                  </p>
                  <Link
                    to="/contact"
                    className="mt-4 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                  >
                    提交核实线索
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </article>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}